import { useState, useEffect, useCallback, useContext } from "react";
import { useHttpClient } from "./http-hook";
import { AuthContext } from "../context/auth-context";

export const useSubscription = () => {
	const { sendRequest } = useHttpClient();
	const auth = useContext(AuthContext);
	const [subscription, setSubscription] = useState(null);
	const [usage, setUsage] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const orgId = auth?.memberships?.[0]?.orgId || null;
	//this typically is with the option for admins to switch orgs, not needed because admins don't use this page (they dont need subscriptions)

	// Fetch subscription & usage info
	const fetchSubscriptionData = useCallback(async () => {
		if (!orgId) return;

		setLoading(true);
		try {
			const response = await sendRequest(
				`/api/organizations/${orgId}/subscription`,
				"GET",
				null,
				{
					Authorization: `Bearer ${auth.token}`,
					"X-Org-Id": orgId,
				}
			);

			const data = response.data;
			setSubscription(data.subscription);
			setUsage({
				usedSeats: data.memberCount,
				allowedSeats: data.subscription?.seats || 0,
				utilizationPercentage: data.subscription?.seats
					? Math.round((data.memberCount / data.subscription.seats) * 100)
					: 0,
			});
			setError(null);
		} catch (err) {
			console.error("Error fetching subscription data:", err.message);
			setError(err.message || "Failed to fetch subscription data");
		} finally {
			setLoading(false);
		}
	}, [orgId, sendRequest, auth.token]);

	useEffect(() => {
		fetchSubscriptionData();
	}, [fetchSubscriptionData]);


	// POST to open Stripe portal
	const openCustomerPortal = async () => {
		try {
			const response = await sendRequest(
				`/api/subscriptions/${orgId}/portal`,
				"POST",
				null,
				{
					Authorization: `Bearer ${auth.token}`,
					"X-Org-Id": orgId,
				}
			);

			const { url } = response.data;
			window.open(url, "_blank");
		} catch (err) {
			console.error("Error opening customer portal:", err.message);
			setError(err.message || "Failed to open customer portal");
		}
	};

	const canAddUser = () => usage && usage.usedSeats < usage.allowedSeats;

	const isAtLimit = () => usage && usage.usedSeats >= usage.allowedSeats;

	return {
		subscription,
		usage,
		loading,
		error,
		openCustomerPortal,
		canAddUser,
		isAtLimit,
		orgId,
		refresh: fetchSubscriptionData,
	};
};
